export function activityTimestamp(value) {
  if (value instanceof Date) return value.getTime();
  const raw = String(value || "").trim();
  if (!raw) return NaN;
  return Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : `${raw.replace(" ", "T")}Z`);
}

export function activityBoardState(metadata = {}, { approving = false, stopping = false } = {}) {
  // Booking is complete once the Discovery Call has a calendar event, even
  // when a legacy scheduling task still carries review workflow flags.
  if (String(metadata.review_kind || "").trim().toLowerCase() === "discovery_call" &&
      String(metadata.calendar_event_id || "").trim()) return "complete";
  const review = String(metadata.review_status || "").trim().toLowerCase();
  const workflow = String(metadata.workflow_status || metadata.status || "").trim().toLowerCase();
  if (review === "rejected") return "rejected";
  if (review === "approved") return "complete";
  if (workflow === "failed") return "failed";
  const finished = ["completed", "complete", "done", "succeeded"].includes(workflow) || Number(metadata.progress ?? metadata.percent_complete ?? 0) >= 100;
  if (finished && (review !== "needs_review" || workflow === "approving")) return "complete";
  if (approving || stopping || workflow === "approving") return "progress";
  const running = ["running", "queued", "pending", "in_progress", "processing", "formatting"].includes(workflow);
  const proposal = ["company_contact", "company_job", "company_lead"].includes(metadata.review_kind);
  if (review === "needs_review" && (proposal || !running)) return "review";
  if (Number(metadata.progress ?? metadata.percent_complete ?? 0) >= 100) return "complete";
  return running ? "progress" : "complete";
}

export function mergeActivitySnapshot(current, incoming) {
  const previousById = new Map(current.map((activity) => [String(activity.id), activity]));
  return incoming.map((activity) => {
    const previous = previousById.get(String(activity.id));
    if (!previous) return activity;
    const preserveHistory = () => ({ ...previous, ...Object.fromEntries(
      ["entity_status", "current_recruiting_stage", "current_sequence_status", "current_sequence_name"]
        .filter(key => Object.prototype.hasOwnProperty.call(activity, key))
        .map(key => [key, activity[key]])
    ) });
    const previousTime = activityTimestamp(previous.updated_at || previous.created_at) || 0;
    const incomingTime = activityTimestamp(activity.updated_at || activity.created_at) || 0;
    if (incomingTime < previousTime) return preserveHistory();
    // Database timestamps can tie within a second; a late poll must not undo approval.
    if (incomingTime === previousTime) {
      if (activityBoardState(previous.metadata) === "complete" && activityBoardState(activity.metadata) === "progress") return preserveHistory();
      const oldReview = previous.metadata?.review_status;
      const newReview = activity.metadata?.review_status;
      if (["approved", "rejected"].includes(oldReview) && newReview === "needs_review") return preserveHistory();
      if (previous.metadata?.workflow_status === "approving" && newReview === "needs_review" &&
          activityBoardState(activity.metadata) === "review") return preserveHistory();
    }
    return activity;
  });
}

export function activityCompletionTime(activity) {
  const metadata = activity.metadata || {};
  return [metadata.approved_at, metadata.completed_at, activity.updated_at, activity.created_at]
    .map(activityTimestamp)
    .find(Number.isFinite) || 0;
}
